/**
 * 学生端"我是哪个学生"的解析 —— 把 **auth_user.id** 正确翻译成 **student.id**。
 *
 * 为什么必须有这一层（这是契约对齐里专门点名的一处隐患）：
 *  - 学生登录后，`/auth/me` 给的 `user.id` 是 **auth_user.id**；
 *  - 而 `/students/:id/nodes`、`/students/:id/points`、`/student/today` 等接口
 *    要的是 **student.id**（`student` 表的主键，见 packages/db/src/schema.ts：
 *    `student.user_id → auth_user.id`，但 `student.id` 是另一个 uuid）。
 *  - 两者都是 uuid，**TypeScript 查不出混用**，混了就是运行期 404。
 *
 * 所以学生端统一从这里拿 id：
 *   `GET /students`（后端对学生会返回他自己那一条）→ 用 `userId` 对上号 → 取 `id`。
 *
 * 兼容性考虑：后端返回体里的字段名在契约里没有逐字写死，因此这里按顺序尝试
 * `userId` / `authUserId` / `user_id` 三个键；一个都对不上时才退一步
 * 用 `GET /students/:authUserId` 直接问后端要（后端 access 层对"学生传自己的
 * auth id"是容忍的），最后仍然失败则如实报错，绝不悄悄用一个可能错的 id 去发请求。
 */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './auth';
import { api, tryRequest } from './api/client';
import { pickArray, pickObject } from './api/shape';
import type { StudentSummary } from './api/types';

/** 从一条学生记录里取出它绑定的 auth_user id（字段名有多种可能）。 */
function authUserIdOf(raw: unknown): string | null {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const candidate = record.userId ?? record.authUserId ?? record.user_id ?? record.auth_user_id;
  return typeof candidate === 'string' ? candidate : null;
}

function normalizeStudent(raw: unknown): StudentSummary | null {
  if (raw === null || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const id = typeof record.id === 'string' ? record.id : '';
  if (!id) return null;
  return {
    id,
    nickname: typeof record.nickname === 'string' ? record.nickname : '',
    username: typeof record.username === 'string' ? record.username : '',
    gender: record.gender === 'female' ? 'female' : 'male',
    birthday: typeof record.birthday === 'string' ? record.birthday : '',
    gradeCode: typeof record.gradeCode === 'string' ? record.gradeCode : '',
    uiModeOverride:
      record.uiModeOverride === 'child' ||
      record.uiModeOverride === 'bridge' ||
      record.uiModeOverride === 'full'
        ? record.uiModeOverride
        : null,
    permission: 'owner',
    // 学生的 auth id 由 authUserIdOf 单独读，这里不必固化到类型上
  };
}

export interface MyStudentResult {
  /** 学生档案 id —— **所有面向学生的接口都用它**。 */
  studentId: string | null;
  student: StudentSummary | null;
  /** 正在解析中（此时不要发业务请求）。 */
  isLoading: boolean;
  /** 解析失败（网络 / 后端异常）。 */
  error: unknown;
  refetch: () => void;
}

export function useMyStudent(): MyStudentResult {
  const { user, isLoading: authLoading } = useAuth();
  const authUserId = user?.role === 'student' ? (user?.id ?? null) : null;

  const query = useQuery({
    queryKey: ['student-self', authUserId],
    queryFn: async (): Promise<StudentSummary | null> => {
      // 1) 走列表：后端对学生会返回他自己那一条
      const listResult = await tryRequest(() => api.get<unknown>('/students'));
      if (listResult.state === 'ok') {
        const rawList = pickArray<unknown>(listResult.data, 'students', 'items');
        const matched = rawList.find((raw) => authUserIdOf(raw) === authUserId);
        if (matched) return normalizeStudent(matched);
        // 列表里没有 authUserId 字段时，学生的列表通常只有他自己一条
        if (rawList.length === 1) return normalizeStudent(rawList[0]);
      }

      // 2) 退一步：直接按 auth id 取详情（后端容忍学生传自己的 auth id）
      const detailResult = await tryRequest(() => api.get<unknown>(`/students/${authUserId}`));
      if (detailResult.state === 'ok') {
        return normalizeStudent(pickObject(detailResult.data, detailResult.data));
      }
      return null;
    },
    enabled: Boolean(authUserId),
    retry: 1,
    staleTime: 5 * 60_000,
  });

  return {
    studentId: query.data?.id ?? null,
    student: query.data ?? null,
    isLoading: authLoading || (Boolean(authUserId) && query.isLoading),
    error: query.error,
    refetch: () => void query.refetch(),
  };
}
